import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface StoreItem {
  id: number;
  vendorCode?: string | null;
  name: string;
  unitId?: number | null;
  unit?: { ucode: string } | null;
  catalogId?: number | null;
  catalog?: { id: number; name: string } | null;
  notes?: string | null;
  isActive: boolean;
  groupId?: number | null;
  stockMin?: number | null;
  stockMax?: number | null;
  level1?: string | null;
  level2?: string | null;
  level3?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const useStoreItems = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<StoreItem> | StoreItem[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<StoreItem>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useStoreItem = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<StoreItem>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

export type ItemHistoryPort = { name: string; code: string | null; countryName: string | null };

export type ItemHistoryEvent = {
  kind: 'requisition' | 'enquiry' | 'quotation' | 'purchase_order' | 'delivery' | 'invoice';
  at: string;
  documentId: number;
  documentRef: string | null;
  caseId: number | null;
  caseRef: string | null;
  port: ItemHistoryPort | null;
  supplier: { name: string; code: string | null } | null;
  currencyCode: string | null;
  qty: number | null;
  unitPrice: number | null;
  lineTotal: number | null;
  href: string;
};

export type ItemHistoryResponse = {
  item: { id: number; vendorCode: string | null; name: string };
  events: ItemHistoryEvent[];
};

export const useStoreItemHistory = (id: number) => {
  const path = id ? `/store-items/${id}/history` : null;
  const { data, error, isLoading, mutate } = useSWR<ItemHistoryResponse>(path, fetcher, { revalidateOnFocus: true });
  return { data, isLoading, isError: error, mutate };
};
